import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyTriggerOverview } from './companyTriggerApi';
import SuppressedPanel from './SuppressedPanel';
import BarList from './TriggerBars';
import {
  StatCard,
  Panel,
  PanelEmpty,
  MiniDonutCard,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import { STATUS_RAMP, AREA_RAMP, RANK_RAMP } from './triggerTheme';

/**
 * Trigger Trace → Overview.
 *
 * Three things carry the page:
 *
 *  1. IS ANYONE USING IT. Take-up, volume, and whether people came back.
 *  2. DO THEY CLOSE WHAT THEY OPEN. This is the feature working or not
 *     working. Logging a trigger is the easy half; coming back to mark it
 *     worked through is the half that means something changed. A section that
 *     only counted logs would show a tool being used and tell you nothing about
 *     whether it helped.
 *  3. WHERE IT COMES FROM. The share of triggers tagged 'Work' is the one
 *     figure on this page an employer can act on directly, so it gets a card —
 *     with copy that says what it is, because it is also the easiest figure
 *     here to misread.
 *
 * WHAT THE RATES DELIBERATELY MEAN. "Worked through" is resolved out of the
 * logs people actually CLOSED, not out of everything they logged. Counting
 * still-open logs as unresolved would make the heaviest users of the tool look
 * like its worst performers, which is exactly backwards. "Still open" is shown
 * separately, as a share of everything, because an open log is unfinished
 * rather than failed.
 *
 * Nobody is named on this page, and nothing anybody typed reaches it.
 *
 * `fetcher` is a prop so an unscoped hub could render this page unchanged.
 */
const TriggerOverview = ({
  fetcher = fetchCompanyTriggerOverview,
  populationLabel = 'users'
}) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (loader) return <Loader />;

  // The whole page is withheld, not just a panel: at this group size every
  // figure on it would be about identifiable people.
  if (!stats || stats.suppressed) {
    return <SuppressedPanel minimum={stats?.minimum ?? 0} />;
  }

  const trendLength = stats.logsTrend.length;
  const trendHasData = stats.logsTrend.some((value) => value > 0);

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { width: [3, 2], curve: 'smooth', dashArray: [0, 4] },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(trendLength), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(Math.round(val)) } },
    tooltip: { shared: true }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: stats.weekday.map((row) => row.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(Math.round(val)) } },
    tooltip: { y: { formatter: (val) => `${formatBigNumber(val)} logged` } }
  };

  const weekdayHasData = stats.weekday.some((row) => row.count > 0);

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Logging"
          value={formatBigNumber(stats.participants)}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={stats.totalUsers > 0 ? `${stats.adoption}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Triggers Logged"
          value={formatBigNumber(stats.logs)}
        />
        {/* Of what people closed — not of everything they logged. */}
        <StatCard
          icon={STAT_ICONS.completions}
          title="Worked Through"
          value={stats.closed > 0 ? `${stats.resolutionRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.more}
          title="Still Open"
          value={formatBigNumber(stats.pending)}
        />
        {/* The one figure here an employer can act on directly. */}
        <StatCard
          icon={STAT_ICONS.score}
          title="Work-Related"
          value={stats.logs > 0 ? `${stats.workShare}%` : '-'}
        />
      </div>

      {/* ---------- Closing the loop, and where it comes from ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="What Happens Next"
          subtitle="Every trigger logged, by where it ended up"
          data={stats.statusSplit}
          colors={STATUS_RAMP}
          unit="logs"
          totalLabel="Logs"
          legendLimit={3}
        />
        <MiniDonutCard
          title="Which Part of Life"
          subtitle="The area each trigger was tagged with"
          data={stats.lifeAreaSplit}
          colors={AREA_RAMP}
          unit="logs"
          totalLabel="Logs"
          legendLimit={8}
        />
        <Panel title="Logging Over Time" subtitle="Triggers logged and people logging — last 30 days">
          {trendHasData ? (
            <Chart
              type="line"
              height={260}
              width="100%"
              series={[
                { name: 'Triggers logged', data: stats.logsTrend },
                { name: 'People logging', data: stats.peopleTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>Nothing logged in the last 30 days.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- What sets people off ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Panel title="Most Common Triggers" subtitle="From the app's own list of eight">
          {/* Anything a person typed themselves is pooled into 'Something else'
              inside the pipeline, so no written name can appear in this list. */}
          {stats.topTriggers.length > 0 ? (
            <BarList
              rows={stats.topTriggers}
              unit="logs"
              colorFor={(index) => RANK_RAMP[index % RANK_RAMP.length]}
            />
          ) : (
            <PanelEmpty>Nothing logged yet.</PanelEmpty>
          )}
        </Panel>
        <Panel title="What It Felt Like" subtitle="Emotions attached to those triggers">
          {stats.topEmotions.length > 0 ? (
            <BarList
              rows={stats.topEmotions}
              unit="logs"
              colorFor={(index) => RANK_RAMP[index % RANK_RAMP.length]}
            />
          ) : (
            <PanelEmpty>No emotions recorded yet.</PanelEmpty>
          )}
          {/* More than one emotion can be attached to a log, so these shares
              are of all logs and will usually add up to more than 100%. */}
          <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
            People can pick more than one feeling per trigger, so these add up to more than 100%.
          </p>
        </Panel>
        <Panel title="When Triggers Happen" subtitle="Day of the week, all time">
          {weekdayHasData ? (
            <Chart
              type="bar"
              height={260}
              width="100%"
              series={[{ name: 'Triggers logged', data: stats.weekday.map((row) => row.count) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>Nothing logged yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Staying with it ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.logs}
          title="Logs per Person"
          value={stats.participants > 0 ? stats.logsPerPerson : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Came Back This Week"
          value={`${stats.weeklyReturnRate}%`}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Days to Work Through"
          value={stats.avgDaysToResolve == null ? '-' : stats.avgDaysToResolve}
        />
        {/* Somebody who has written their own trigger names is using the tool
            more deliberately than somebody working off the eight defaults.
            The names themselves are never read. */}
        <StatCard
          icon={STAT_ICONS.custom}
          title="Wrote Their Own Triggers"
          value={formatBigNumber(stats.customVocabulary.people)}
        />
      </div>

      <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
        &ldquo;Worked through&rdquo; is the share of logs people closed that they marked as resolved,
        not a share of everything they logged — an open trigger is unfinished, not failed. The
        work-related share counts moments people chose to log, which is a measure of the tool being
        used as much as of anything else; read it as a prompt to ask, not as a verdict on a team.
      </p>
      <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
        Nobody is named on this page. The notes people write about what happened are never read by
        this dashboard, and trigger names anybody typed themselves are pooled rather than shown.
      </p>
    </div>
  );
};

TriggerOverview.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default TriggerOverview;
